// TopBar.jsx
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  CiMenuKebab,
  CiMenuBurger,
} from "react-icons/ci";
import {
  AiOutlineFullscreen,
  AiOutlineFullscreenExit,
} from "react-icons/ai";
import { IoMdNotificationsOutline } from "react-icons/io";
import { adminLogout } from "../../Store/Slices/AdminSlice";
import ThemeToggler from "../ThemeToggler/ThemeToggler";
import logo from "../../Assets/images/logo.png";
import { UsersApi } from "../../App/api/users.api";

// New notification slice imports
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  notificationAdded,
} from "../../Store/Slices/NotificationSlice"; // adjust path

function TopBar({ handleSideBar }) {
  const dispatch = useDispatch();

  // ── Redux selectors ───────────────────────────────────
  const { currentUser } = useSelector((state) => state.store);
  const { theme } = useSelector((state) => state.theme); // true = light
  const {
    notifications,
    unreadCount,
    status: notifStatus,
  } = useSelector((state) => state.notifications);

  const userName = currentUser?.data?.user?.first_name || "Admin";

  // ── Local state ───────────────────────────────────────
  const [notifOpen, setNotifOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const notifRef = useRef(null);
  const logoutRef = useRef(null);
  const avatarRef = useRef(null);

  // ── Load notifications on mount ───────────────────────
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  // ── Fullscreen listener ───────────────────────────────
  useEffect(() => {
    const handleChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleChange);
    return () => document.removeEventListener("fullscreenchange", handleChange);
  }, []);

  // ── Close dropdowns on outside click ──────────────────
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (
        logoutRef.current &&
        !logoutRef.current.contains(e.target) &&
        avatarRef.current &&
        !avatarRef.current.contains(e.target)
      ) {
        setLogoutOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Handlers ──────────────────────────────────────────
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.warn);
    } else if (document.exitFullscreen) {
      document.exitFullscreen().catch(console.warn);
    }
  };

  const handleLogout = () => {
    dispatch(adminLogout({ payload: null, token: false }));
  };

  const handleMarkAllRead = () => {
    dispatch(markAllNotificationsAsRead())
      .unwrap()
      .then(() => {
        toast.success("All notifications marked as read");
      })
      .catch(() => {
        toast.error("Failed to mark all as read");
      });
  };

  const handleNotificationClick = (notif) => {
    const id = notif._id || notif.id;
    if (!notif.read && !notif.is_read) {
      dispatch(markNotificationAsRead(id))
        .unwrap()
        .catch(() => {
          toast.error("Could not mark notification as read");
        });
    }
    // Optional: navigate to order/details page, etc.
  };

  // ── Helpers ───────────────────────────────────────────
  const timeAgo = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);

    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const isLoading = notifStatus === "loading";

  const containerBg = theme ? "bg-white text-gray-900" : "bg-gray-900 text-white";
  const dropdownBg = theme
    ? "bg-white border-gray-200 shadow-xl"
    : "bg-gray-800 border-gray-700 shadow-2xl";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-10 flex h-16 items-center justify-between px-4 shadow-md ${containerBg} transition-colors`}
    >
      {/* Sidebar toggle */}
      <button
        onClick={handleSideBar}
        className="flex items-center gap-1 rounded-lg p-2 text-2xl hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
        aria-label="Toggle sidebar"
      >
        <CiMenuKebab />
        <CiMenuBurger className="ml-[-8px]" />
      </button>

      {/* Logo - centered */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <img src={logo} alt="Logo" className="h-10 w-auto" />
      </div>

      {/* Right side actions */}
      <div className="flex items-center gap-5">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setNotifOpen((v) => !v)}
            className="relative flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
            aria-label="Notifications"
          >
            <IoMdNotificationsOutline className="text-2xl" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div
              className={`absolute right-0 top-full z-50 mt-2 w-96 origin-top-right rounded-xl ${dropdownBg} overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10`}
            >
              <div className="flex items-center justify-between border-b border-gray-200/70 px-5 py-3.5 dark:border-gray-700/60">
                <h3 className="text-lg font-semibold">Notifications</h3>
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              <div className="max-h-[70vh] overflow-y-auto">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-t-blue-500"></div>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                    No notifications yet
                  </div>
                ) : (
                  notifications.map((notif) => {
                    const isUnread = !notif.read && !notif.is_read;
                    return (
                      <div
                        key={notif._id || notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className={`cursor-pointer border-b border-gray-100 px-5 py-3.5 last:border-none hover:bg-gray-50/80 dark:border-gray-700/40 dark:hover:bg-gray-700/30 ${
                          isUnread
                            ? "bg-blue-50/60 font-medium dark:bg-blue-950/30"
                            : "text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        <p className="line-clamp-2 leading-snug">
                          {notif.message || notif.title || "New notification"}
                        </p>
                        {notif.createdAt && (
                          <span className="mt-1.5 block text-xs text-gray-500 dark:text-gray-400">
                            {timeAgo(notif.createdAt)}
                          </span>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Theme toggle */}
        <ThemeToggler />

        {/* Fullscreen toggle */}
        <button
          onClick={toggleFullscreen}
          className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-gray-200/50 dark:hover:bg-gray-700/50"
          aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
        >
          {isFullscreen ? (
            <AiOutlineFullscreenExit className="text-xl" />
          ) : (
            <AiOutlineFullscreen className="text-xl" />
          )}
        </button>

        {/* User avatar & logout */}
        <div className="relative flex items-center gap-3">
          <span className="hidden font-medium md:block">{userName}</span>

          <button
            ref={avatarRef}
            onClick={() => setLogoutOpen((v) => !v)}
            className="h-9 w-9 overflow-hidden rounded-full ring-2 ring-gray-300/70 transition hover:ring-gray-400 dark:ring-gray-600 dark:hover:ring-gray-500"
          >
            <img
              src={logo}
              alt="User avatar"
              className="h-full w-full object-cover"
            />
          </button>

          {logoutOpen && (
            <div
              ref={logoutRef}
              className={`absolute right-0 top-full z-50 mt-2 w-48 origin-top-right rounded-xl ${dropdownBg} py-1 shadow-2xl ring-1 ring-black/5 dark:ring-white/10`}
            >
              <button
                onClick={handleLogout}
                className="block w-full px-5 py-3 text-left text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

export default TopBar;